import { Box, Fade, Grid, makeStyles, Typography, useMediaQuery } from "@material-ui/core";
import Lottie from "lottie-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import downsideMitigationAnimation from "../../../../animations/downsideMitigation.json";
import topRatedServiceAnimation from "../../../../animations/topRatedService.json";
import { useSharedStyles } from "../../../../common/sharedStyles";
import { useIsInViewport } from "../../../../hooks";
import { ReactComponent as AdultMaterialIcon } from "../../../../images/adult-material.svg";
import { ReactComponent as AlcoholIcon } from "../../../../images/alcohol.svg";
import { ReactComponent as ArmamentsIcon } from "../../../../images/armaments.svg";
import { ReactComponent as CivilianFirearmsIcon } from "../../../../images/civilian-firearms.svg";
import { ReactComponent as ControversialWeaponsIcon } from "../../../../images/controversial-weapons.svg";
import { ReactComponent as CorruptionIcon } from "../../../../images/corruption.svg";
import { ReactComponent as EnvironmentalDestructionIcon } from "../../../../images/environmental-destruction.svg";
import { ReactComponent as FossilFuelsIcon } from "../../../../images/fossil-fuels.svg";
import { ReactComponent as GamblingIcon } from "../../../../images/gambling.svg";
import { ReactComponent as HumanRightsAbusesIcon } from "../../../../images/human-rights-abuses.svg";
import { ReactComponent as LabourRightsViolationsIcon } from "../../../../images/labour-rights-violations.svg";
import { ReactComponent as TobaccoIcon } from "../../../../images/tobacco.svg";
import theme from "../../../../theme";
import { Colors, HintText, SubtitleText, TitleText } from "../../../controls/helpers";
import { Body } from "../../GridLayout";
import QuestionnaireScreen from "../../QuestionnaireScreen";

const ICONS = [
  { Icon: AdultMaterialIcon, title: "Adult Material" },
  { Icon: AlcoholIcon, title: "Alcohol" },
  { Icon: ArmamentsIcon, title: "Armaments" },
  { Icon: CivilianFirearmsIcon, title: "Civilian Firearms" },
  { Icon: ControversialWeaponsIcon, title: "Controversial Weapons" },
  { Icon: CorruptionIcon, title: "Corruption" },
  { Icon: EnvironmentalDestructionIcon, title: "Environmental Destruction" },
  { Icon: FossilFuelsIcon, title: "Fossil Fuels" },
  { Icon: GamblingIcon, title: "Gambling" },
  { Icon: HumanRightsAbusesIcon, title: "Human Rights Abuses" },
  { Icon: LabourRightsViolationsIcon, title: "Labour Rights Violations" },
  { Icon: TobaccoIcon, title: "Tobacco" },
];

const LearnMore = () => {
  const classes = useStyles();
  const sharedClasses = useSharedStyles();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <QuestionnaireScreen>
      <Body isNarrowView className={classes.body}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} className={classes.hero}>
                <Lottie animationData={downsideMitigationAnimation} className={classes.downsideMitigationAnimation} loop={false} />
              </Grid>
              <Grid item xs={12} className={classes.content}>
                <TitleText>We actively mitigate downside risk.</TitleText>
                <SubtitleText>
                  During the Covid-19 sell-off between 1 January and 23 March 2020, NZ Funds KiwiSaver Growth Strategy fell 10% less than the market index thanks to our downside mitigation strategies.
                </SubtitleText>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} className={classes.hero}>
                <Lottie animationData={topRatedServiceAnimation} autoPlay={false} className={classes.topRatedAnimation} loop={false} />
              </Grid>
              <Grid item xs={12} className={classes.content}>
                <TitleText>We are top rated for our service.</TitleText>
                <SubtitleText>NZ Funds KiwiSaver Scheme was rated first for Service &amp; Communications by Sorted in 2018, 2019, 2020, 2021, 2022, 2023.</SubtitleText>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12} className={classes.hero}>
                <AnimationManagementFees />
              </Grid>
              <Grid item xs={12} className={classes.content}>
                <TitleText>Our management fees are transparent and fair.</TitleText>
                <SubtitleText>NZ Funds' fees are fair and transparent and vary depending on which Strategies you are invested in.</SubtitleText>
                <Box mt={6} textAlign="center">
                  <Typography className={sharedClasses.smallPrint}>
                    *Fees are forward looking estimates as set out in the PDS. They include forward looking performance fee estimates. Actual fees and performance may vary significantly. The latest
                    actual fee charged is set out in our quarterly Fund Updates. In addition an administration fee is charged and is disclosed in our offer documents. We do not charge entry, exit or
                    account closure fees.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid
                item
                xs={12}
                className={classes.hero}
                style={{ paddingBottom: isExtraSmallScreen ? 9 : 6, paddingLeft: isExtraSmallScreen ? 18 : 60, paddingRight: isExtraSmallScreen ? 18 : 60, paddingTop: 24 }}
              >
                <Grid container justifyContent="center" spacing={0}>
                  {ICONS.map(({ Icon, title }) => (
                    <AnimationIconWithText key={title} paddingBottom={isExtraSmallScreen ? 15 : 18} title={title}>
                      <Icon width={isExtraSmallScreen ? 42 : 54} height={isExtraSmallScreen ? 42 : 54} />
                    </AnimationIconWithText>
                  ))}
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.content}>
                <TitleText>All our funds are responsibly invested.</TitleText>
                <SubtitleText>NZ Funds has one of New Zealand's most comprehensive responsible investment policies, which guides all of our investment decisions.</SubtitleText>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Body>
    </QuestionnaireScreen>
  );
};

function AnimationManagementFees() {
  const sharedClasses = useSharedStyles();
  const ref = useRef(null);
  const [hasBeenInViewport, setHasBeenInViewport] = useState(false);
  const isInViewport = useIsInViewport(ref);
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));

  useEffect(() => {
    !hasBeenInViewport && isInViewport && setHasBeenInViewport(true);
  }, [hasBeenInViewport, isInViewport]);

  return (
    <div ref={ref}>
      <Fade exit={false} in={hasBeenInViewport} timeout={1200}>
        <Box alignItems="center" display="flex" flexDirection="column">
          <TitleText bold className={sharedClasses.white} hideMarginBottom variant={isExtraSmallScreen ? "h4" : "h3"}>
            0.00% – 1.65% p.a*
          </TitleText>
        </Box>
      </Fade>
    </div>
  );
}

function AnimationIconWithText({ children, paddingBottom, title }: { children: ReactNode; paddingBottom: number; title: ReactNode }) {
  const ref = useRef(null);
  const [hasBeenInViewport, setHasBeenInViewport] = useState(false);
  const isInViewport = useIsInViewport(ref);

  useEffect(() => {
    !hasBeenInViewport && isInViewport && setHasBeenInViewport(true);
  }, [hasBeenInViewport, isInViewport]);

  return (
    <Grid item ref={ref} xs={4} sm={3} style={{ paddingBottom }}>
      <Fade exit={false} in={hasBeenInViewport} timeout={1200}>
        <Box alignItems="center" display="flex" flexDirection="column">
          {children}
          <HintText color={Colors.White} textAlign="center" variant="caption">
            {title}
          </HintText>
        </Box>
      </Fade>
    </Grid>
  );
}

const useStyles = makeStyles(
  {
    body: {
      padding: "0 !important",
    },
    topRatedAnimation: {
      marginTop: -9,
      width: 210,
      height: 210,
      "& path": {
        fill: Colors.White,
        stroke: Colors.White,
      },
    },
    downsideMitigationAnimation: {
      width: 320,
      height: 320,
      "& path": {
        fill: Colors.White,
        stroke: Colors.White,
      },
    },
    hero: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 285,
      backgroundColor: Colors.Blue,
    },
    content: {
      padding: 24,
      minHeight: 250,
    },
  },
  { name: "LearnMore" }
);

export default LearnMore;
